import { useState } from 'react'
import { PALETTE as P } from '../tokens'
import { useAuth } from '../context/AuthContext'
import { authApi } from '../api/auth'
import {
  MONTHS,
  SEX_OPTIONS,
  MIN_AGE,
  dayOptions,
  yearOptions,
  daysIn,
  assembleDob,
  ageFromDob,
  formatDob,
} from '../utils/dob'

/**
 * BackfillModal — one-time demographics ask for existing users who
 * predate the onboarding gate (``user.date_of_birth === null``). Gated by
 * ProtectedRoute and rendered over the first app screen, BEFORE the
 * research-consent modal.
 *
 * DOB is required; sex is optional. Two submit paths, branching on a
 * client-side age check (the server re-checks authoritatively):
 *   - 18+   → PATCH /auth/me  { date_of_birth, sex }
 *   - <18   → a confirmation step (mistype recovery), then
 *             POST /auth/age-restrict { date_of_birth } — the server sets
 *             the restriction flag and DISCARDS the DOB; ProtectedRoute
 *             then swaps in the locked view.
 *
 * No dismiss / skip affordance: DOB is required.
 */
export function BackfillModal() {
  const { token, patchUser } = useAuth()

  const [dobMonth, setDobMonth] = useState('')
  const [dobDay, setDobDay] = useState('')
  const [dobYear, setDobYear] = useState('')
  const [sex, setSex] = useState(null)
  const [phase, setPhase] = useState('form')   // 'form' | 'confirmMinor'
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)

  const dobIso = assembleDob(dobMonth, dobDay, dobYear)

  const selectStyle = {
    width: '100%',
    background: P.surface,
    border: `1.5px solid ${P.border}`,
    borderRadius: 14,
    padding: '13px 12px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    fontWeight: 300,
    color: P.text,
    outline: 'none',
  }

  // 18+ → save via PATCH /auth/me. <18 → go to confirmation (no API call
  // yet, so a mistyped year is recoverable).
  async function handleSave() {
    if (!dobIso || submitting) return
    setError(false)
    if (ageFromDob(dobIso) < MIN_AGE) {
      setPhase('confirmMinor')
      return
    }
    setSubmitting(true)
    try {
      await authApi.updateProfile(
        { date_of_birth: dobIso, sex: sex || null },
        token,
      )
      // Patch only after the write succeeds so the modal doesn't dismiss
      // on a failed save (DOB is required — we can't let them through
      // without it).
      patchUser({ date_of_birth: dobIso, sex: sex || null })
    } catch {
      setError(true)
      setSubmitting(false)
    }
  }

  // Commit the restriction. Only reachable after the user confirms the
  // under-18 DOB was not a typo.
  async function handleConfirmRestrict() {
    if (!dobIso || submitting) return
    setError(false)
    setSubmitting(true)
    try {
      await authApi.ageRestrict(dobIso, token)
      // Server set age_restricted + discarded the DOB. Flip local state so
      // ProtectedRoute renders the locked view.
      patchUser({ age_restricted: true })
    } catch {
      setError(true)
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(28,24,20,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
      animation: 'fadeIn 0.25s ease',
      fontFamily: "'DM Sans', sans-serif",
      overflowY: 'auto',
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bfRise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="backfill-title"
        style={{
          background: P.bg,
          borderRadius: 22,
          padding: '30px 26px 24px',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 12px 44px rgba(28,24,20,0.22)',
          animation: 'bfRise 0.32s ease forwards',
        }}
      >
        {phase === 'form' ? (
          <>
            <div style={{
              fontSize: 10, color: P.primary,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 500, marginBottom: 14, textAlign: 'center',
            }}>
              A few details
            </div>

            <h2
              id="backfill-title"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 25, fontStyle: 'italic', fontWeight: 400,
                color: P.text, lineHeight: 1.2, marginBottom: 12,
                textAlign: 'center',
              }}
            >
              A couple of basics.
            </h2>

            <p style={{
              fontSize: 13, color: P.textMid, fontWeight: 300,
              lineHeight: 1.65, marginBottom: 22, textAlign: 'center',
            }}>
              Help Haiven read your signal in context. Your date of birth is
              required; sharing your sex is optional.
            </p>

            {/* Date of birth — US month / day / year order */}
            <label
              htmlFor="bf-dob-month"
              style={{ display: 'block', fontSize: 13, color: P.textMid, fontWeight: 400, marginBottom: 10 }}
            >
              When were you born?
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.1fr', gap: 8, marginBottom: 22 }}>
              <select
                id="bf-dob-month"
                aria-label="Birth month"
                style={selectStyle}
                value={dobMonth}
                onChange={(e) => {
                  const m = e.target.value
                  setDobMonth(m)
                  if (dobDay && Number(dobDay) > daysIn(m, dobYear)) setDobDay('')
                }}
              >
                <option value="">Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                aria-label="Birth day"
                style={selectStyle}
                value={dobDay}
                onChange={(e) => setDobDay(e.target.value)}
              >
                <option value="">Day</option>
                {dayOptions(dobMonth, dobYear).map((d) => (
                  <option key={d} value={d}>{Number(d)}</option>
                ))}
              </select>
              <select
                aria-label="Birth year"
                style={selectStyle}
                value={dobYear}
                onChange={(e) => {
                  const y = e.target.value
                  setDobYear(y)
                  if (dobDay && Number(dobDay) > daysIn(dobMonth, y)) setDobDay('')
                }}
              >
                <option value="">Year</option>
                {yearOptions().map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* Biological sex — optional */}
            <div style={{ fontSize: 13, color: P.textMid, fontWeight: 400, marginBottom: 10 }}>
              Sex <span style={{ color: P.textFaint }}>&middot; optional</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
              {SEX_OPTIONS.map((opt) => (
                <div
                  key={opt.id}
                  onClick={() => setSex(opt.id)}
                  style={{
                    background: sex === opt.id ? P.primarySoft : P.surface,
                    border: `1.5px solid ${sex === opt.id ? P.primary : P.border}`,
                    borderRadius: 14,
                    padding: '13px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{
                    width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${sex === opt.id ? P.primary : P.border}`,
                    background: sex === opt.id ? P.primary : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {sex === opt.id && (
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: P.surface }} />
                    )}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 300, color: P.text }}>{opt.label}</span>
                </div>
              ))}
            </div>

            {error && (
              <p style={{ fontSize: 12, color: P.primary, textAlign: 'center', marginBottom: 14, fontWeight: 300 }}>
                Something went wrong. Please try again.
              </p>
            )}

            <button
              onClick={handleSave}
              disabled={!dobIso || submitting}
              style={{
                width: '100%', padding: 15,
                background: P.dark, border: 'none', borderRadius: 14,
                cursor: (!dobIso || submitting) ? 'default' : 'pointer',
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 16, fontStyle: 'italic', color: P.bg,
                letterSpacing: '0.01em',
                opacity: (!dobIso || submitting) ? 0.4 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              Save
            </button>
          </>
        ) : (
          <>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 24, fontStyle: 'italic', fontWeight: 400,
                color: P.text, lineHeight: 1.2, marginBottom: 14,
                textAlign: 'center',
              }}
            >
              Just checking.
            </h2>
            <p style={{
              fontSize: 13.5, color: P.textMid, fontWeight: 300,
              lineHeight: 1.7, marginBottom: 22, textAlign: 'center',
            }}>
              You entered <strong style={{ fontWeight: 400, color: P.text }}>{formatDob(dobMonth, dobDay, dobYear)}</strong>,
              which is under {MIN_AGE}. Haiven is currently for adults {MIN_AGE} and over.
              If that date is a mistake, go back and fix it.
            </p>

            {error && (
              <p style={{ fontSize: 12, color: P.primary, textAlign: 'center', marginBottom: 14, fontWeight: 300 }}>
                Something went wrong. Please try again.
              </p>
            )}

            <button
              onClick={() => { setError(false); setPhase('form') }}
              disabled={submitting}
              style={{
                width: '100%', padding: 14,
                background: P.dark, border: 'none', borderRadius: 14,
                cursor: submitting ? 'default' : 'pointer',
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 16, fontStyle: 'italic', color: P.bg,
                marginBottom: 10, opacity: submitting ? 0.5 : 1,
              }}
            >
              Go back and edit
            </button>
            <button
              onClick={handleConfirmRestrict}
              disabled={submitting}
              style={{
                width: '100%', padding: 13,
                background: 'none', border: `1.5px solid ${P.border}`,
                borderRadius: 14,
                cursor: submitting ? 'default' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13, fontWeight: 400, color: P.textMute,
                opacity: submitting ? 0.5 : 1,
              }}
            >
              That date is correct
            </button>
          </>
        )}
      </div>
    </div>
  )
}
